import { type Dispatch, type SetStateAction, useCallback } from "react";
import { useChosenDepartment } from "../../../../contexts/ChosenDepartment";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useReadRosterNurse } from "../../../../hooks/rosterCache";
import { trackEvent } from "../../../../utils/analytics";
import type { DayOff, Nurse } from "../../../../utils/models";
import { daysOffAndPtoToNursePreferences } from "../../../../utils/utilFunctions";

type Props = {
	nurse?: Nurse;
	nurseId?: number;
	setNurseData?: Dispatch<SetStateAction<Nurse>>;
	onSaved?: () => void;
};

export function useEditDayOff({ nurse, nurseId, setNurseData, onSaved }: Props) {
	// updateNurse patches both Staffing's copy on the provider and the roster query; it moves off the provider in step 5.
	const { updateNurse } = useHospitalData();
	const { chosenDepartment } = useChosenDepartment();
	const readRosterNurse = useReadRosterNurse();

	const handleEditSave = useCallback(
		(updatedDayOff: DayOff) => {
			const nurseToUse = nurse ?? (nurseId !== undefined ? readRosterNurse(chosenDepartment.department_id, nurseId) : undefined);
			if (nurseToUse) {
				updateNurse({
					...nurseToUse,
					employeeSpecificPreferences: nurseToUse.employeeSpecificPreferences.map((pref) =>
						pref.id === updatedDayOff.id ? daysOffAndPtoToNursePreferences([updatedDayOff], nurseToUse)[0] : pref,
					),
				});
			}
			setNurseData?.((prevNurse) => ({
				...prevNurse,
				employeeSpecificPreferences: prevNurse.employeeSpecificPreferences.map((pref) =>
					pref.id === updatedDayOff.id ? daysOffAndPtoToNursePreferences([updatedDayOff], prevNurse)[0] : pref,
				),
			}));
			trackEvent("day_off_updated", {
				nurse_id: nurseToUse?.id ?? nurseId,
				date_from: updatedDayOff.dateFrom?.toFormat("yyyy-MM-dd"),
				date_to: updatedDayOff.dateTo?.toFormat("yyyy-MM-dd"),
			});
			onSaved?.();
		},
		[nurse, nurseId, updateNurse, readRosterNurse, chosenDepartment.department_id, setNurseData, onSaved],
	);

	return { handleEditSave };
}
